import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBrand } from "@/context/BrandContext";
import {
  ArrowRight, CheckCircle, X, List, Phone, Star, ShieldCheck, Headset,
  GlobeHemisphereEast, Wallet, Lightning, Crown, House, Buildings,
} from "@phosphor-icons/react";

const C = {
  bg: "#070B16", surface: "#0F1729", card: "#111a2e", green: "#10B981", orange: "#FF7A00",
  text: "#F8FAFC", sub: "#94A3B8", border: "rgba(255,255,255,0.10)", wa: "#25D366",
};
const HEAD = { fontFamily: "'Outfit', sans-serif" };
const BODY = { fontFamily: "'Manrope', sans-serif" };
const SUPPORT_WA = "https://wa.me/12029403399?text=" + encodeURIComponent("Hi! I'd like help choosing an iYeneh plan.");

const PLANS = [
  {
    key: "residential",
    name: "Residential",
    nick: "The Global Family Bridge",
    price: "19.99",
    icon: House,
    accent: C.green,
    headline: "Stop paying for bad connections. Hear home in HD.",
    cta: "Bridge the distance",
    features: [
      "1 Global Number",
      "3 Extensions",
      "500 US / Canada minutes",
      "$5 Free Calling Credit",
      "24/7 SI Smart Greeting",
    ],
  },
  {
    key: "business",
    name: "Business",
    nick: "The 24/7 Executive Office",
    price: "49.99",
    icon: Buildings,
    accent: C.orange,
    popular: true,
    headline: "Look like a billion-dollar company for the price of a coffee.",
    cta: "Command your office",
    features: [
      "2 Global Numbers",
      "5 Extensions",
      "1,500 US / Canada minutes",
      "$20 Free Calling Credit",
      "SIP Hardware Support",
      "The SI Sales Closer",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    nick: "The Unlimited Global Powerhouse",
    price: "129.99",
    icon: Crown,
    accent: "#A78BFA",
    headline: "Own the market. No boundaries. No limits.",
    cta: "Take the throne",
    features: [
      "5 Global Numbers",
      "10 Extensions",
      "5,000 US / Canada minutes",
      "$75 Free Calling Credit",
      "Full SI Sales Team",
      "Call Recording",
      "Simultaneous Multi-Device Sync",
    ],
  },
];

const MATRIX = [
  { label: "Global Numbers", vals: ["1", "2", "5"] },
  { label: "Extensions", vals: ["3", "5", "10"] },
  { label: "US / Canada minutes", vals: ["500", "1,500", "5,000"] },
  { label: "Free Calling Credit", vals: ["$5", "$20", "$75"] },
  { label: "24/7 SI Smart Greeting", vals: [true, true, true] },
  { label: "SIP Hardware Support", vals: [false, true, true] },
  { label: "SI Sales Closer", vals: [false, true, true] },
  { label: "Full SI Sales Team", vals: [false, false, true] },
  { label: "Call Recording", vals: [false, false, true] },
  { label: "Simultaneous Multi-Device Sync", vals: [false, false, true] },
  { label: "Overage auto-billed from Wallet", vals: [true, true, true] },
];

export default function Pricing() {
  const brand = useBrand();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const choose = (key) => {
    try { localStorage.setItem("iyeneh_plan_intent", key); } catch {}
    navigate(`/checkout?plan=${key}`);
  };

  return (
    <div style={{ ...BODY, background: C.bg, color: C.text }} className="min-h-screen overflow-x-hidden" data-testid="pricing-page">
      {/* NAV */}
      <header className="sticky top-0 z-50 border-b bg-[#0B1220]/85 backdrop-blur-xl" style={{ borderColor: C.border }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" data-testid="pricing-logo" className="flex items-center gap-2">
            <img src="/icons/icon-512.png" alt="iYeneh Connect" className="h-9 w-9 sm:h-10 sm:w-10" />
            <span className="text-lg font-bold tracking-tight text-white sm:text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>iYeneh Connect</span>
          </Link>
          <nav className="hidden items-center gap-7 md:flex">
            <Link to="/services" className="text-sm font-medium text-slate-300 hover:text-white">Services</Link>
            <Link to="/rates" className="text-sm font-medium text-slate-300 hover:text-white">Rates</Link>
            <Link to="/pricing" className="text-sm font-semibold text-white">Plans</Link>
            <a href={SUPPORT_WA} target="_blank" rel="noreferrer" className="text-sm font-medium text-slate-300 hover:text-white">Support</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" data-testid="pricing-signin" className="hidden text-sm font-semibold text-slate-200 hover:text-white sm:block">Sign In</Link>
            <Link to="/register" data-testid="pricing-create-account" className="rounded-full px-4 py-2.5 text-sm font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5" style={{ background: C.orange }}>Create Free Account</Link>
            <button onClick={() => setMenu((m) => !m)} className="text-slate-200 md:hidden" aria-label="Menu">{menu ? <X size={24} /> : <List size={24} />}</button>
          </div>
        </div>
        {menu && (
          <div className="border-t md:hidden" style={{ borderColor: C.border }}>
            <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3">
              <Link to="/services" onClick={() => setMenu(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/5">Services</Link>
              <Link to="/rates" onClick={() => setMenu(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/5">Rates</Link>
              <a href={SUPPORT_WA} target="_blank" rel="noreferrer" className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/5">Support</a>
              <Link to="/login" onClick={() => setMenu(false)} className="rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-200 hover:bg-white/5">Sign In</Link>
            </nav>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="mx-auto max-w-4xl px-4 pt-14 pb-6 text-center sm:px-6 sm:pt-20">
        <div className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: "#FF7A001a", color: C.orange }}>
          <Star size={14} weight="fill" /> Premium Global Telecom &amp; eFax Hub
        </div>
        <h1 style={HEAD} className="text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          Pick your power level.<br /><span style={{ color: C.orange }}>Talk to the world.</span>
        </h1>
        <p style={{ color: C.sub }} className="mx-auto mt-5 max-w-2xl text-base leading-relaxed sm:text-lg">
          One number that rings every device. Crystal-clear HD voice, instant eFax, and an SI team that answers, greets and closes — while you live your life. No contracts. Cancel anytime.
        </p>
      </section>

      {/* PLAN CARDS */}
      <section className="mx-auto grid max-w-6xl gap-6 px-4 pb-6 pt-4 sm:px-6 lg:grid-cols-3">
        {PLANS.map((p) => {
          const Icon = p.icon;
          return (
            <div
              key={p.key}
              data-testid={`plan-card-${p.key}`}
              className={`relative flex flex-col rounded-3xl border p-7 transition-transform hover:-translate-y-1 ${p.popular ? "lg:-mt-4 lg:mb-4 lg:scale-[1.03]" : ""}`}
              style={{
                background: p.popular ? "linear-gradient(180deg,#16233f 0%,#0F1729 100%)" : C.card,
                borderColor: p.popular ? C.orange : C.border,
                boxShadow: p.popular ? "0 20px 60px -20px rgba(255,122,0,0.45)" : "none",
              }}
            >
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-4 py-1 text-xs font-extrabold uppercase tracking-wide text-white" style={{ background: C.orange }}>
                  Most Popular
                </span>
              )}
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${p.accent}22`, color: p.accent }}>
                  <Icon size={22} weight="fill" />
                </div>
                <div>
                  <p style={HEAD} className="text-lg font-extrabold">{p.name}</p>
                  <p className="text-xs font-semibold" style={{ color: p.accent }}>{p.nick}</p>
                </div>
              </div>

              <div className="mt-6 flex items-end gap-1">
                <span style={HEAD} className="text-5xl font-black">${p.price}</span>
                <span className="mb-1.5 text-sm" style={{ color: C.sub }}>/month</span>
              </div>

              <p style={HEAD} className="mt-4 min-h-[3.5rem] text-lg font-bold leading-snug">
                “{p.headline}”
              </p>

              <button
                data-testid={`plan-cta-${p.key}`}
                onClick={() => choose(p.key)}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-bold text-white transition-transform hover:scale-[1.02]"
                style={{ background: p.popular ? C.orange : "rgba(255,255,255,0.08)", border: p.popular ? "none" : `1px solid ${C.border}` }}
              >
                {p.cta} <ArrowRight size={18} weight="bold" />
              </button>

              <ul className="mt-6 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <CheckCircle size={18} weight="fill" style={{ color: p.accent }} className="mt-0.5 shrink-0" />
                    <span className="text-slate-200">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>

      {/* OVERAGE / FAIR USE NOTE */}
      <section className="mx-auto max-w-6xl px-4 pb-10 sm:px-6">
        <div className="flex items-start gap-3 rounded-2xl border p-5" style={{ borderColor: C.border, background: C.surface }}>
          <Wallet size={22} weight="fill" style={{ color: C.green }} className="mt-0.5 shrink-0" />
          <p className="text-sm leading-relaxed" style={{ color: C.sub }}>
            <span className="font-semibold text-slate-100">Never get cut off.</span> Go over your minutes or extensions and it just works — extra usage is automatically drawn from your iYeneh Wallet at our published per-destination rate. Top up once and talk without limits.
          </p>
        </div>
      </section>

      {/* COMPARISON MATRIX */}
      <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <h2 style={HEAD} className="mb-6 text-center text-2xl font-extrabold sm:text-3xl">Compare every plan</h2>
        <div className="overflow-hidden rounded-2xl border" style={{ borderColor: C.border }}>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[540px] text-left text-sm" data-testid="pricing-matrix">
            <thead>
              <tr style={{ background: C.surface }}>
                <th className="px-4 py-4 font-semibold text-slate-300 sm:px-6">Feature</th>
                {PLANS.map((p) => (
                  <th key={p.key} className="px-3 py-4 text-center sm:px-4">
                    <span style={HEAD} className="block font-extrabold">{p.name}</span>
                    <span className="text-xs font-semibold" style={{ color: p.accent }}>${p.price}/mo</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MATRIX.map((row, i) => (
                <tr key={row.label} style={{ background: i % 2 ? "transparent" : "rgba(255,255,255,0.02)" }}>
                  <td className="px-4 py-3.5 font-medium text-slate-200 sm:px-6">{row.label}</td>
                  {row.vals.map((v, idx) => (
                    <td key={idx} className="px-3 py-3.5 text-center sm:px-4">
                      {typeof v === "boolean"
                        ? (v ? <CheckCircle size={20} weight="fill" style={{ color: C.green }} className="mx-auto" /> : <X size={16} className="mx-auto text-slate-600" />)
                        : <span className="font-semibold text-slate-100">{v}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        <p className="mt-4 text-center text-xs" style={{ color: C.sub }}>
          All plans billed monthly. Cancel anytime. Overage billed from your iYeneh Wallet.
        </p>
      </section>

      {/* TRUST STRIP */}
      <section className="border-t" style={{ borderColor: C.border, background: C.surface }}>
        <div className="mx-auto grid max-w-6xl gap-6 px-4 py-12 sm:grid-cols-3 sm:px-6">
          {[
            { icon: ShieldCheck, t: "Carrier-grade & secure", d: "Encrypted calls, encrypted checkout, real reliability." },
            { icon: GlobeHemisphereEast, t: "Truly global", d: "Ring home and the world from any device, anywhere." },
            { icon: Headset, t: "Real human support", d: "Plus a 24/7 SI team that never sleeps." },
          ].map((b) => (
            <div key={b.t} className="flex items-start gap-3">
              <b.icon size={24} weight="fill" style={{ color: C.orange }} className="mt-0.5 shrink-0" />
              <div>
                <p style={HEAD} className="font-bold">{b.t}</p>
                <p className="text-sm" style={{ color: C.sub }}>{b.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
        <Phone size={36} weight="fill" style={{ color: C.orange }} className="mx-auto" />
        <h2 style={HEAD} className="mt-4 text-3xl font-black sm:text-4xl">Your global office is one click away.</h2>
        <p className="mx-auto mt-3 max-w-xl text-base" style={{ color: C.sub }}>Start today. Set up in minutes. Cancel anytime.</p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <button data-testid="pricing-final-cta" onClick={() => choose("business")} className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold text-white shadow-xl transition-transform hover:-translate-y-1" style={{ background: C.orange }}>
            Get started <ArrowRight size={18} weight="bold" />
          </button>
          <a href={SUPPORT_WA} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 px-8 py-4 font-semibold text-white transition-colors hover:bg-white/10" style={{ borderColor: "rgba(255,255,255,0.4)" }}>
            <Lightning size={18} weight="fill" /> Talk to sales
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t px-4 py-6 text-center text-xs sm:px-6" style={{ borderColor: C.border, color: C.sub, background: "#0B1220" }}>
        © {new Date().getFullYear()} {brand.legal_name || brand.brand_name || "Capital Defense Institute LLC"} ·{" "}
        <Link to="/privacy" className="hover:text-white">Privacy</Link> ·{" "}
        <Link to="/terms" className="hover:text-white">Terms</Link> ·{" "}
        <Link to="/services" className="hover:text-white">Services</Link>
      </footer>
    </div>
  );
}
